import React, { useContext, useState } from "react";
import { Calendar, Edit01, HelpCircle, MarkerPin01, InfoCircle } from "@untitled-ui/icons-react";
import moment from "moment";
import classNames from "classnames";
import { Button, Tooltip } from "@myhealth-raccoon/components";

import { useSelector } from 'src/store';
import DoctorIcon from 'src/assets/doctor.svg?react';
import { isAfterHours, isFaceToFace } from "src/utils/appointmentTypes";
import { useAppointmentTypeSelector, useClinicSelector, useDoctorSelector } from "src/hooks/useAsyncSelector";
import { XClose } from "@untitled-ui/icons-react";
import { ConfirmClose } from "./modals";
import { availabilityPickerContext } from "src/contexts/availabilityPicker";
import { clinicInfoContext } from "src/contexts/clinicInfo";
import { cancellationPolicyContext } from "src/contexts/cancellationPolicy";
import { clinicName, doctorName } from "src/utils/name";
import { innerHtml } from "src/utils/html";
import { AfterHoursPricingDetails } from "./AfterHoursPricingDetails";
import { useShouldHideCloseButton } from "src/hooks/shouldHideCloseButton";

export function AppointmentSidebar() {
    const { setOpen: setEditingAppointmentTime } = useContext(availabilityPickerContext)

    const { setOpen: setViewingClinicInfo } = useContext(clinicInfoContext);
    const { setOpen: setViewingCancellationPolicy } = useContext(cancellationPolicyContext);
    const hideCloseButton = useShouldHideCloseButton();

    const { dateTime, reservedBooking } = useSelector((state) => state.appointment);
    const { data: clinic, ...clinicRequest } = useClinicSelector();
    const { data: doctor, ...doctorRequest } = useDoctorSelector();
    const { data: appointmentType, ...appointmentTypeRequest } = useAppointmentTypeSelector();
    const [showConfirmClose, setShowConfirmClose] = useState(false);
    const closeConfirm = () => {
        setShowConfirmClose(false);
    }

    return <div className="flex flex-col gap:4 lg:gap-8">
        <div className={classNames("flex flex-row items-start", { "animate-pulse": doctorRequest.isLoading })}>
            <div className='flex-shrink-0'>
                <span className='flex justify-center items-center bg-gray-300 rounded-full w-10 h-10 lg:w-14 lg:h-14 overflow-hidden'>
                    {doctor &&
                        <img className="animate-fade-in w-full h-full object-fit-cover"
                            role="presentation"
                            src={doctor.umbracoProfileUrl ? `${import.meta.env.VITE_MYHEALTH_WEBSITE}${doctor.umbracoProfileUrl}` : doctor.automedProfileUrl}
                        />}
                </span>
            </div>

            <div className='ml-4 flex-1 overflow-hidden'>
                <h3 className={classNames('font-medium text-gray-700 text-base lg:text-lg leading-5', {
                    'min-h-[2lh] lg:min-h-0 flex items-center': appointmentType && !isFaceToFace(appointmentType)
                })}>
                    {doctorRequest.isLoading
                        ? <span className="w-32 opacity-25 inline-block bg-current rounded-full h-[0.5em]"></span>
                        : doctor ? doctorName(doctor) : doctorRequest.error?.message
                    }
                </h3>
                <div className='font-base text-sm lg:text-base text-gray-600'>
                    <p className="hidden lg:block">
                        {doctorRequest.isLoading
                            ? <span className="w-40 opacity-25 inline-block bg-current rounded-full h-[0.5em]"></span>
                            : doctor ? doctor.umbracoRole : ""}
                    </p>
                    {appointmentType && isFaceToFace(appointmentType) &&
                        <div className="lg:hidden flex flex-row gap-2 mt-1 overflow-hidden">
                            {clinicRequest.isLoading
                                ? <span className="w-40 opacity-25 inline-block bg-current rounded-full h-[0.5em]"></span>
                                : <span className="text-ellipsis whitespace-nowrap overflow-hidden">
                                    {clinicName(clinic)}
                                </span>}

                            <button className={classNames("text-sm text-gray-600 font-semibold", {
                                "pointer-events-none opacity-25": clinicRequest.isLoading || !clinic
                            })} disabled={clinicRequest.isLoading} onClick={() => setViewingClinicInfo(true)}>
                                <InfoCircle className="size-4 inline" />
                            </button>
                        </div>}
                </div>
            </div>
            {!hideCloseButton && <XClose className="flex h-6 w-6 cursor-pointer lg:hidden" onClick={() => setShowConfirmClose(true)} />}
        </div>
        <div className="space-y-4">
            <h3 className='hidden lg:block font-semibold text-lg text-gray-900'>Appointment Details</h3>
            <ul className="space-y-4">
                <li className="flex w-full flex-row relative gap-2.5">
                    <DoctorIcon className="w-6 h-6 text-gray-400" />
                    <div>
                        {appointmentTypeRequest.isLoading
                            ? <span className="w-40 opacity-25 inline-block bg-current rounded-full h-[0.5em]"></span>
                            : appointmentType
                                ? <>
                                    <p className='text-sm lg:text-base text-gray-700 font-medium'>
                                        {isFaceToFace(appointmentType) ? "Face to Face" : "Telehealth"}
                                        <br className="hidden lg:block" />
                                        <span className="md:hidden"> - </span>
                                        <span className="text-gray-600 font-medium">
                                            {appointmentType.appointmentName}
                                            {' '}
                                            {/* For now we are hiding the ApptType Info until further design review (21/1/25) */}
                                            {appointmentType.apptInstructionsInHTML &&
                                                <Tooltip
                                                    tooltipContent={<div {...innerHtml(appointmentType.apptInstructionsInHTML)} />}
                                                    attachmentPoint='top'
                                                    className='inline-block align-middle hidden'
                                                >
                                                    <HelpCircle className='w-4 h-4 text-gray-400' />
                                                </Tooltip>
                                            }
                                        </span>
                                    </p>
                                </>
                                : appointmentTypeRequest?.error?.message}
                    </div>
                </li>
                <li className="flex w-full flex-row relative gap-2.5 items-center lg:items-start">
                    <Calendar className="w-6 h-6 text-gray-400" />
                    <div className="flex-1 text-sm lg:text-base text-gray-700 font-medium">
                        <p className="hidden lg:block">
                            {moment(dateTime).format("dddd, D MMMM YYYY")}<br />
                            {moment(dateTime).format("LT")}
                        </p>
                        <p className="block lg:hidden">
                            {moment(dateTime).format("ddd, D MMMM [at] LT")}
                        </p>
                    </div>
                    <Button variant="link" className="flex gap-2 -my-4 lg:m-0"
                        disabled={!!reservedBooking}
                        onClick={() => setEditingAppointmentTime(true)}
                    >
                        <Edit01 className="w-5 h-5 " />
                        Modify
                    </Button>
                </li>
                {appointmentType && isFaceToFace(appointmentType) &&
                    <li className={classNames("hidden lg:flex w-full flex-row relative gap-2.5", {
                        "animate-pulse": clinicRequest.isLoading
                    })}>
                        <MarkerPin01 className="w-6 h-6 text-gray-400" />
                        <div>
                            <p className='text-base text-gray-700 font-medium'>
                                {clinicRequest.isLoading
                                    ? <span className="w-40 opacity-25 inline-block bg-current rounded-full h-[0.5em]"></span>
                                    : clinic ? clinicName(clinic) : clinicRequest.error?.message}
                            </p>
                            <button className={classNames("mt-1 text-sm text-gray-600 font-semibold underline", {
                                "pointer-events-none opacity-25": clinicRequest.isLoading || !clinic
                            })} disabled={clinicRequest.isLoading} onClick={() => setViewingClinicInfo(true)}>
                                View info
                            </button>
                        </div>
                    </li>}
                <li className="lg:hidden">
                    <button className="text-gray-600 text-sm underline font-semibold" onClick={() => setViewingCancellationPolicy(true)}>View Pricing & Cancellation Policy Info</button>
                </li>
            </ul>
        </div>
        <div className="hidden lg:block rounded-xl bg-white px-4 py-6 border border-gray-200">
            {appointmentType && isAfterHours(appointmentType)
                ?
                <>
                    <h4 className="text-gray-900 text-base font-semibold mb-1">Pricing details</h4>
                    <AfterHoursPricingDetails appointmentType={appointmentType} />
                    <hr className="my-4" />
                    This is a private service. You will not receive any Medicare Rebates for this consultation.
                </>
                : <>
                    <h4 className="text-gray-900 text-base font-semibold mb-1">Pricing details</h4>
                    <div className="space-y-2.5 text-gray-700">
                        <p>
                            Full payment is required at consultation. If applicable, the Medicare rebate will be processed onsite.
                        </p>
                        <p>
                            A surcharge of 1.28% + 35c applies for card payments.
                        </p>
                        <p>
                            <strong className="font-semibold">For detailed fee policy, view the <a target="_blank" className="text-primary" href={`${import.meta.env.VITE_MYHEALTH_WEBSITE}${clinic?.url}#pricing`}>centre's pricing policy</a>.</strong>
                        </p>
                    </div>
                    <hr className="my-4" />
                    {clinic?.cancellationPolicyHtml && <>
                        <h4 className="text-gray-900 text-base font-semibold mb-1">Cancellation policy</h4>
                        <div className="prose-sm text-gray-700" {...innerHtml(clinic.cancellationPolicyHtml)} />
                    </>}
                </>}</div>
        <ConfirmClose
            isOpen={showConfirmClose}
            onClose={closeConfirm}
        />
    </div>
}